import { DatePipe } from '@angular/common';
import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { AppStore } from '@app/app.store';
import { TimezoneService } from '@app/core/services/user/timezone.service';

abstract class VfsDatePipe {
  /**
   * The constructor
   *
   * @param appStore the app store
   */
  constructor(
    @Inject(LOCALE_ID) protected locale: string,
    protected appStore: AppStore,
    protected timezoneService: TimezoneService
  ) {}

  /**
   * Transform a value
   *
   * @param value value to transform (could be null)
   * @param format optional format
   * @return transformed value
   */
  transform(value, format): string | null {
    if (value) {
      const datePipe = new DatePipe(this.appStore.state.session.locale || this.locale);
      // If the format is defined, just use it. If not defined, get the store format but provide a default if not found
      const timezone = this.appStore.state.session.user.timezone
        ? this.timezoneService.getUtcOffset(this.appStore.state.session.user.timezone)
        : '+00:00';
      if (format === 'short') {
        format = 'dd/MM/yyyy HH:mm';
      }
      return datePipe.transform(value, format, timezone);
    } else {
      return '';
    }
  }
}

/**
 * A pipe to transform UTC dates to a given format
 *
 * How to use:
 *   {{myutcdate | UTCDate}}
 */
@Pipe({ name: 'UTCDate' })
export class UTCDatePipe extends VfsDatePipe implements PipeTransform {
  /**
   * The constructor
   *
   * @param appStore the app store
   */
  constructor(@Inject(LOCALE_ID) locale: string, appStore: AppStore, protected timezoneService: TimezoneService) {
    super(locale, appStore, timezoneService);
  }

  /**
   * Transform a value
   *
   * @param value value to transform (could be null)
   * @param format optional format. If defined has the highest priority
   * @return transformed value
   */
  transform(value, format?) {
    return super.transform(value, format ? format : 'dd/MM/yyyy HH:mm:ss');
  }
}

@Pipe({ name: 'UTCShortDate' })
export class UTCShortDatePipe extends VfsDatePipe implements PipeTransform {
  /**
   * The constructor
   *
   * @param appStore the app store
   */
  constructor(@Inject(LOCALE_ID) locale: string, appStore: AppStore, protected timezoneService: TimezoneService) {
    super(locale, appStore, timezoneService);
  }

  /**
   * Transform a value
   *
   * @param value value to transform (could be null)
   * @param format optional format. If defined has the highest priority
   * @return transformed value
   */
  transform(value, format?): string | null {
    return super.transform(value, format || 'shortDate');
  }
}
@Pipe({ name: 'UTCSecondsToTime' })
export class UTCSecondsToTime extends VfsDatePipe implements PipeTransform {
  /**
   * The constructor
   *
   * @param appStore the app store
   */
  constructor(@Inject(LOCALE_ID) locale: string, appStore: AppStore, protected timezoneService: TimezoneService) {
    super(locale, appStore, timezoneService);
  }

  /**
   * Transform a value
   *
   * @param value value to transform (could be null)
   * @param format optional format. If defined has the highest priority
   * @return transformed value
   */
  transform(value, format?) {
    if (!isNaN(value)) {
      let dateObj = new Date(value * 1000);
      let hours = dateObj.getUTCHours();
      let minutes = dateObj.getUTCMinutes();
      let seconds = dateObj.getSeconds();

      return (
        hours.toString().padStart(2, '0') +
        ':' +
        minutes.toString().padStart(2, '0') +
        ':' +
        seconds.toString().padStart(2, '0')
      );
    } else {
      return '';
    }
  }
}

@Pipe({ name: 'UTCDateNoTime' })
export class UTCDateNoTime extends VfsDatePipe implements PipeTransform {
  /**
   * The constructor
   *
   * @param appStore the app store
   */
  constructor(@Inject(LOCALE_ID) locale: string, appStore: AppStore, protected timezoneService: TimezoneService) {
    super(locale, appStore, timezoneService);
  }

  /**
   * Transform a value
   *
   * @param value value to transform (could be null)
   * @param format optional format. If defined has the highest priority
   * @return transformed value
   */
  transform(value, format?): string | null {
    return super.transform(value, 'dd/MM/yyyy');
  }
}
